// ./src/components/features/AccountEmails/AccountEmailConfirmModal.tsx
import React, { useState, useEffect } from "react";
import { Stack, Text, TextInput, Alert } from "@mantine/core";
import { IconMail } from "@tabler/icons-react";
import { AccountEmail } from "@/utils/api";
import { AppDrawer, AppDrawerAction } from "@/components/generic";

export interface AccountEmailConfirmModalProps {
  /** Whether the drawer is open */
  opened: boolean;

  /** Function to close the drawer */
  onClose: () => void;

  /** Account email to confirm */
  accountEmail?: AccountEmail | null;

  /** Callback to send confirmation email */
  onSendConfirmation?: (accountEmail: AccountEmail) => Promise<boolean>;

  /** Callback to verify confirmation code */
  onVerifyCode?: (accountEmail: AccountEmail, code: string) => Promise<boolean>;

  /** Whether send operation is in progress */
  isSending?: boolean;

  /** Whether verify operation is in progress */
  isVerifying?: boolean;

  /** Last error from operations */
  lastError?: string | null;

  /** Callback to clear errors */
  onClearError?: () => void;
}

type ConfirmationStep = "initial" | "code-sent";

export const AccountEmailConfirmModal: React.FC<
  AccountEmailConfirmModalProps
> = ({
  opened,
  onClose,
  accountEmail,
  onSendConfirmation,
  onVerifyCode,
  isSending = false,
  isVerifying = false,
  lastError,
  onClearError,
}) => {
  const [step, setStep] = useState<ConfirmationStep>("initial");
  const [confirmationCode, setConfirmationCode] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);

  const isProcessing = isSending || isVerifying;

  // Reset state when drawer opens/closes
  useEffect(() => {
    if (opened) {
      setStep("initial");
      setConfirmationCode("");
      setValidationError(null);
      onClearError?.();
    }
  }, [opened, onClearError]);

  // Handle send confirmation email
  const handleSendConfirmation = async () => {
    if (!accountEmail || !onSendConfirmation) return;

    onClearError?.();
    setValidationError(null);

    try {
      const success = await onSendConfirmation(accountEmail);
      if (success) {
        setStep("code-sent");
      }
    } catch (error) {
      console.error("Send confirmation error:", error);
    }
  };

  // Handle verify code
  const handleVerifyCode = async () => {
    if (!accountEmail || !onVerifyCode) return;

    // Validate code
    const trimmedCode = confirmationCode.trim();
    if (!trimmedCode) {
      setValidationError("Please enter the confirmation code");
      return;
    }

    if (trimmedCode.length < 4) {
      setValidationError("Please enter a valid confirmation code");
      return;
    }

    onClearError?.();
    setValidationError(null);

    try {
      const success = await onVerifyCode(accountEmail, trimmedCode);
      if (success) {
        onClose();
      }
    } catch (error) {
      console.error("Verify code error:", error);
    }
  };

  // Handle code input change
  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmationCode(e.target.value);
    if (validationError) {
      setValidationError(null);
    }
  };

  // Render initial step - explain and send
  const renderInitialStep = () => (
    <Stack gap="md">
      <Alert icon={<IconMail size={20} />} color="blue" variant="light">
        To use this email address for notifications and workflows, we need to
        verify that you have access to it.
      </Alert>

      <Text size="sm">We will send a confirmation code to:</Text>

      <Text fw={600} size="md" ta="center" py="xs">
        {accountEmail?.email}
      </Text>

      <Text size="sm" c="dimmed">
        Please check your inbox (and spam folder) for an email containing the
        confirmation code. The code will expire in 24 hours.
      </Text>
    </Stack>
  );

  // Render code entry step
  const renderCodeSentStep = () => (
    <Stack gap="md">
      <Alert icon={<IconMail size={20} />} color="green" variant="light">
        A confirmation code has been sent to{" "}
        <strong>{accountEmail?.email}</strong>
      </Alert>

      <Text size="sm">
        Please enter the confirmation code from the email below:
      </Text>

      <TextInput
        label="Confirmation Code"
        placeholder="Enter code"
        value={confirmationCode}
        onChange={handleCodeChange}
        error={validationError}
        size="md"
        autoFocus
        styles={{
          input: {
            textAlign: "center",
            fontSize: "1.2rem",
            letterSpacing: "0.2rem",
          },
        }}
      />

      <Text size="xs" c="dimmed" ta="center">
        Didn't receive the email? Check your spam folder or{" "}
        <Text
          component="span"
          c="blue"
          style={{ cursor: isProcessing ? "default" : "pointer" }}
          onClick={isProcessing ? undefined : handleSendConfirmation}
        >
          resend the code
        </Text>
      </Text>
    </Stack>
  );

  // Build actions based on current step
  const getActions = (): AppDrawerAction[] => {
    const cancelAction: AppDrawerAction = {
      label: "Cancel",
      variant: "secondary",
      onClick: onClose,
      disabled: isProcessing,
    };

    if (step === "initial") {
      return [
        cancelAction,
        {
          label: "Send Confirmation Email",
          variant: "primary",
          iconName: "Mail",
          onClick: handleSendConfirmation,
          inProgress: isSending,
          disabled: isProcessing,
        },
      ];
    } else {
      return [
        cancelAction,
        {
          label: "Verify Code",
          variant: "primary",
          iconName: "Check",
          onClick: handleVerifyCode,
          inProgress: isVerifying,
          disabled: isProcessing,
        },
      ];
    }
  };

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title="Confirm Email Address"
      size="md"
      position="right"
      closable={!isProcessing}
      loading={isProcessing}
      errors={lastError}
      actions={getActions()}
    >
      {step === "initial" && renderInitialStep()}
      {step === "code-sent" && renderCodeSentStep()}
    </AppDrawer>
  );
};
